import React, { useState, useEffect } from 'react';

const InteractiveQuestionCard = ({ 
  questionData, 
  onResponse, 
  loading, 
  currentStep,
  totalSteps 
}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [sliderValue, setSliderValue] = useState(questionData.defaultValue || 50);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [dragItems, setDragItems] = useState(questionData.items || []);
  const [isAnimating, setIsAnimating] = useState(false);

  // Initialize drag items
  useEffect(() => {
    if (questionData.items) {
      setDragItems(questionData.items);
    }
  }, [questionData]);

  // Handle card selection
  const handleCardSelect = (option) => {
    if (loading) return;
    
    setSelectedValue(option.id);
    setIsAnimating(true);
    
    // Animate selection and submit after delay
    setTimeout(() => {
      onResponse({
        type: 'card_select',
        id: option.id,
        title: option.title,
        subtitle: option.subtitle,
        icon: option.icon,
        points: option.points
      });
    }, 600);
  };

  // Handle slider change
  const handleSliderSubmit = () => {
    if (loading) return;
    
    const label = getSliderLabel(sliderValue);
    onResponse({
      type: 'slider',
      value: sliderValue,
      label: label,
      points: Math.max(10, Math.floor(sliderValue / 10))
    });
  };

  // Get slider label based on value
  const getSliderLabel = (value) => {
    if (!questionData.marks) return '';
    
    const closestMark = questionData.marks.reduce((closest, mark) => {
      return Math.abs(mark.value - value) < Math.abs(closest.value - value) ? mark : closest;
    });
    
    return closestMark.label;
  };

  // Handle icon grid selection
  const handleIconToggle = (option) => {
    if (loading) return;
    
    setSelectedOptions(prev => {
      const isSelected = prev.some(item => item.id === option.id);
      let newSelection;
      
      if (isSelected) {
        newSelection = prev.filter(item => item.id !== option.id);
      } else {
        if (prev.length >= questionData.maxSelections) {
          // Replace oldest selection
          newSelection = [...prev.slice(1), option];
        } else {
          newSelection = [...prev, option];
        }
      }
      
      return newSelection;
    });
  };

  const handleIconGridSubmit = () => {
    if (selectedOptions.length < questionData.minSelections || loading) return;
    
    onResponse({
      type: 'icon_grid',
      selections: selectedOptions,
      points: selectedOptions.length * 3
    });
  };

  // Handle drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination || loading) return;
    
    const items = Array.from(dragItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setDragItems(items);
  };

  const handleDragDropSubmit = () => {
    if (loading) return;
    
    onResponse({
      type: 'drag_drop',
      rankedItems: dragItems,
      points: 15
    });
  };

  // Handle multiple choice
  const handleMultiChoice = (option) => {
    if (loading) return;
    
    setSelectedValue(option.id);
    setIsAnimating(true);
    
    setTimeout(() => {
      onResponse({
        type: 'multi_choice',
        id: option.id,
        text: option.text,
        emoji: option.emoji,
        riskLevel: option.riskLevel,
        points: option.points
      });
    }, 600);
  };

  // Handle next button for chat type
  const handleNextButton = () => {
    if (loading) return;
    
    onResponse({
      type: 'next',
      text: 'Continue',
      points: 5
    });
  };

  // Render based on question type
  const renderQuestion = () => {
    switch (questionData.type) {
      
      case 'chat':
        return (
          <div className="chat-question-card">
            <div className="chat-content">
              <div className="chat-animation">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
            <div className="chat-actions">
              <button 
                className="next-button-enhanced"
                onClick={handleNextButton}
                disabled={loading}
              >
                {questionData.nextButtonText || "Continue"} ✨
              </button>
            </div>
          </div>
        );

      case 'card_select':
        return (
          <div className="card-select-question">
            <div className="cards-grid">
              {questionData.options.map((option, index) => (
                <div
                  key={option.id}
                  className={`option-card ${selectedValue === option.id ? 'selected' : ''} ${isAnimating && selectedValue === option.id ? 'animating' : ''}`}
                  onClick={() => handleCardSelect(option)}
                  style={{
                    '--card-color': option.color,
                    '--delay': `${index * 100}ms`
                  }}
                >
                  <div className="card-icon">{option.icon}</div>
                  <div className="card-title">{option.title}</div>
                  <div className="card-subtitle">{option.subtitle}</div>
                  <div className="card-points">+{option.points} pts</div>
                  <div className="card-selection-indicator">
                    <div className="selection-checkmark">✓</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'visual_scale':
      case 'slider':
        return (
          <div className="slider-question">
            <div className="slider-container">
              <div className="slider-labels">
                <div className="slider-label-left">
                  <span className="label-icon">{questionData.leftIcon}</span>
                  <span className="label-text">{questionData.leftLabel}</span>
                </div>
                <div className="slider-label-right">
                  <span className="label-text">{questionData.rightLabel}</span>
                  <span className="label-icon">{questionData.rightIcon}</span>
                </div>
              </div>
              
              <div className="slider-track">
                <input
                  type="range"
                  min={questionData.minValue}
                  max={questionData.maxValue}
                  value={sliderValue}
                  onChange={(e) => setSliderValue(parseInt(e.target.value))}
                  className="slider-input"
                />
                <div className="slider-fill" style={{ width: `${sliderValue}%` }}></div>
                <div className="slider-thumb" style={{ left: `${sliderValue}%` }}>
                  <div className="thumb-value">{sliderValue}</div>
                </div>
              </div>

              {questionData.marks && (
                <div className="slider-marks">
                  {questionData.marks.map((mark) => (
                    <div
                      key={mark.value}
                      className={`slider-mark ${sliderValue >= mark.value - 10 && sliderValue <= mark.value + 10 ? 'active' : ''}`}
                      style={{ left: `${mark.value}%` }}
                    >
                      <div className="mark-dot"></div>
                      <div className="mark-label">{mark.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="slider-actions">
              <button 
                className="submit-slider-btn"
                onClick={handleSliderSubmit}
                disabled={loading}
              >
                That's me! ({getSliderLabel(sliderValue)}) ✓
              </button>
            </div>
          </div>
        );

      case 'icon_grid':
        return (
          <div className="icon-grid-question">
            <div className="selection-counter">
              Selected: {selectedOptions.length} / {questionData.maxSelections}
              {selectedOptions.length < questionData.minSelections && 
                <span className="counter-hint"> (pick at least {questionData.minSelections})</span>
              }
            </div>
            
            <div className="icon-grid">
              {questionData.options.map((option) => {
                const isSelected = selectedOptions.some(item => item.id === option.id);
                return (
                  <div
                    key={option.id}
                    className={`icon-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleIconToggle(option)}
                  >
                    <div className="option-icon">{option.icon}</div>
                    <div className="option-name">{option.name}</div>
                    <div className="option-ticker">{option.ticker}</div>
                    {isSelected && (
                      <div className="selection-badge">
                        <span className="selection-number">
                          {selectedOptions.findIndex(item => item.id === option.id) + 1}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="icon-grid-actions">
              <button 
                className="submit-icon-grid-btn"
                onClick={handleIconGridSubmit}
                disabled={selectedOptions.length < questionData.minSelections || loading}
              >
                Perfect choices! 🎯
              </button>
            </div>
          </div>
        );

      case 'drag_drop':
        return (
          <div className="drag-drop-question">
            <div className="drag-instructions">
              <span className="drag-icon">👆</span>
              <span className="drag-text">Click to reorder by importance (drag & drop coming soon)</span>
            </div>
            
            <div className="simple-drag-list">
              {dragItems.map((item, index) => (
                <div
                  key={item.id}
                  className="drag-item clickable"
                  style={{ '--item-color': item.color }}
                  onClick={() => {
                    // Simple click-to-move-up functionality
                    if (index > 0) {
                      const newItems = [...dragItems];
                      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
                      setDragItems(newItems);
                    }
                  }}
                >
                  <div className="drag-rank">#{index + 1}</div>
                  <div className="drag-icon">{item.icon}</div>
                  <div className="drag-text">{item.text}</div>
                  <div className="drag-handle">
                    {index > 0 ? '↑' : '⋮⋮'}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="drag-drop-actions">
              <button 
                className="submit-drag-drop-btn"
                onClick={handleDragDropSubmit}
                disabled={loading}
              >
                That's my priority! 🎯
              </button>
            </div>
          </div>
        );

      case 'multi_choice':
        return (
          <div className="multi-choice-question">
            <div className="multi-choice-options">
              {questionData.options.map((option, index) => (
                <div
                  key={option.id}
                  className={`multi-choice-option ${selectedValue === option.id ? 'selected' : ''} ${isAnimating && selectedValue === option.id ? 'animating' : ''}`}
                  onClick={() => handleMultiChoice(option)}
                  style={{
                    '--option-delay': `${index * 100}ms`
                  }}
                >
                  <div className="option-emoji">{option.emoji}</div>
                  <div className="option-text">{option.text}</div>
                  <div className="option-points">+{option.points} pts</div>
                  <div className="option-selection">
                    <div className="selection-ring"></div>
                    <div className="selection-dot"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="fallback-question">
            <div className="fallback-text">
              Question type not implemented: {questionData.type}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="interactive-question-card">
      <div className="question-content">
        {renderQuestion()}
      </div>
      
      {/* Progress indicator for this question */}
      <div className="question-progress">
        <div className="progress-dots">
          {Array.from({ length: totalSteps }, (_, index) => (
            <div
              key={index}
              className={`progress-dot ${index <= currentStep ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractiveQuestionCard;